import React, { useState } from 'react';
import { User } from 'firebase/auth';
import { authService } from '../services/authService';

interface EmailVerificationBannerProps {
  user: User | null;
}

const EmailVerificationBanner: React.FC<EmailVerificationBannerProps> = ({ user }) => {
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [lastSentTime, setLastSentTime] = useState<number | null>(null);

  if (!user) return null;

  const handleResendVerification = async () => {
    // Prevent spam - 60 second cooldown
    const now = Date.now();
    if (lastSentTime && (now - lastSentTime) < 60000) {
      const remainingSeconds = Math.ceil((60000 - (now - lastSentTime)) / 1000);
      setMessage(`⏱️ Please wait ${remainingSeconds}s before resending`);
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    setSending(true);
    setMessage('');

    try {
      await authService.resendEmailVerification();
      setLastSentTime(now);
      setMessage('✅ Verification email sent! Check your inbox.');
      setTimeout(() => setMessage(''), 5000);
    } catch (error: any) {
      console.error('Error sending verification email:', error);
      setMessage('❌ Failed to send email. Try again later.');
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setSending(false);
    }
  };

  const handleCheckVerification = async () => {
    try {
      await user.reload();
      if (user.emailVerified) {
        // Force page refresh to update UI
        window.location.reload();
      } else {
        setMessage('⚠️ Email not verified yet. Check your inbox.');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error checking verification:', error);
    }
  };

  return (
    <div className="w-full bg-gradient-to-r from-yellow-900/90 via-orange-900/90 to-yellow-900/90 border-b-2 border-yellow-500/50 shadow-lg backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          {/* Left side - Warning message */}
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <span className="text-2xl animate-pulse">⚠️</span>
            </div>
            <div className="flex-1">
              <p className="font-rajdhani text-white font-bold text-sm sm:text-base">
                ⚡ Email Verification Required
              </p>
              <p className="font-rajdhani text-gray-300 text-xs sm:text-sm">
                Please verify your email: <span className="text-yellow-300 font-bold">{user.email}</span>
              </p>
            </div>
          </div>

          {/* Right side - Action buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleResendVerification}
              disabled={sending}
              className="font-orbitron text-xs sm:text-sm px-3 sm:px-4 py-2 bg-yellow-600 hover:bg-yellow-500 disabled:bg-gray-600 text-white rounded uppercase tracking-wider transition-all shadow-md hover:shadow-lg disabled:cursor-not-allowed"
            >
              {sending ? '📧 Sending...' : '📧 Resend Email'}
            </button>
            <button
              onClick={handleCheckVerification}
              className="font-orbitron text-xs sm:text-sm px-3 sm:px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded uppercase tracking-wider transition-all shadow-md hover:shadow-lg"
            >
              ✓ I Verified
            </button>
          </div>
        </div>

        {/* Status message */}
        {message && (
          <div className="mt-2 text-center">
            <p className="font-rajdhani text-sm text-white bg-black/30 inline-block px-4 py-1 rounded">
              {message}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailVerificationBanner;

